<template>
  <div class="signup-page">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">
          <li @click="goToPage('SignupPage')">Sign Up</li>
          <li @click="goToPage('WelcomePage')">Welcome</li>
        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">Sign Up</h1>
      <p class="page-sub">Create your account to get started</p>
    </div>

    <div class="page-wrapper">
      <div class="form-card">

        <form @submit.prevent="submitForm">

          <div class="form-group">
            <label class="form-label">Full Name</label>
            <input class="form-input" v-model="form.fullname" type="text" placeholder="Full Name" required />
          </div>

          <div class="form-group">
            <label class="form-label">Phone Number</label>
            <input
              class="form-input"
              v-model="form.phone_number"
              type="text"
              placeholder="10-digit number"
              required
              pattern="\d{10}"
              title="Please enter exactly 10 digits"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Email</label>
            <input class="form-input" v-model="form.email" type="email" placeholder="Email" required />
          </div>

          <div class="form-group">
            <label class="location-toggle">
              <span class="form-label" style="margin:0;">Enter location manually</span>
              <input type="checkbox" v-model="manualLocation" style="width:18px;height:18px;accent-color:#ba71ff;cursor:pointer;" />
            </label>

            <div v-if="manualLocation" style="margin-top:10px;">
              <input
                class="form-input"
                v-model="manualPlace"
                type="text"
                placeholder="Enter city, place, or address"
              />
            </div>
            <div v-else style="margin-top:10px;">
              <button class="location-btn" type="button" @click="detectLocation">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><circle cx="12" cy="12" r="3"></circle><path d="M12 2v3M12 19v3M2 12h3M19 12h3"></path></svg>
                Use My Location
              </button>
              <p v-if="autoLocation" class="location-success">✅ Location detected</p>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">User Type</label>
            <div class="pill-group">
              <button
                class="pill-option"
                type="button"
                :class="{ 'pill-active': form.user_type === 'Pet Owner' }"
                @click="form.user_type = 'Pet Owner'"
              >Pet Owner</button>
              <button
                class="pill-option"
                type="button"
                :class="{ 'pill-active': form.user_type === 'Pet Lover' }"
                @click="form.user_type = 'Pet Lover'"
              >Pet Lover</button>
            </div>
          </div>

          <button class="submit-btn" type="submit">Register</button>

        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'SignupPage',
  props: {
    initialFullname: { type: String, default: '' },
    initialEmail: { type: String, default: '' },
    user: { 
      type: Object, 
      default: () => ({ fullname: '', phone_number: '' })
    }
  },
  emits: ['user-registered','change-page'],
  data() {
    return {
      form: {
        fullname: this.initialFullname,
        phone_number: '',
        email: this.initialEmail,
        user_type: '',
        location: '',
        location_coords: ''
      },
      manualLocation: false,
      manualPlace: '',
      autoLocation: null,
      sidebarOpen: false
    };
  },
  watch: {
    initialEmail(val) {
      this.form.email = val;
    },
    initialFullname(val) {
      this.form.fullname = val;
    }
  },
  methods: {
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    detectLocation() {
      if (!navigator.geolocation) {
        return console.log('Geolocation not supported');
      }
      navigator.geolocation.getCurrentPosition(
        pos => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          this.autoLocation = { lat, lon };
          this.form.location_coords = `${lat},${lon}`;
          this.form.location = `${lat},${lon}`;
        },
        err => console.log('Location error: ' + err.message)
      );
    },
    async getCoordsFromPlace(place) {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place)}&format=json&limit=1`
      );
      const results = await resp.json();
      if (!results.length) throw new Error('Place not found');
      const { lat, lon } = results[0];
      return { lat, lon };
    },
    async submitForm() {
      if (!this.form.fullname.trim() || !this.form.email.trim()) {
        return alert('Please complete the form');
      }
      if (!this.form.user_type) {
        return alert('Please select Pet Owner or Pet Lover');  // ← this might be your actual bug
      }
      const phone = this.form.phone_number.trim()
      if (!/^\d{10}$/.test(phone)) {
        return alert('Phone number must be exactly 10 digits and contain only numbers.')
      }
      // stick the trimmed value back
      this.form.phone_number = phone

      try {
        // Resolve location
        if (this.manualLocation) {
          if (!this.manualPlace.trim()) {
            return alert('Please enter a location');
          }
          const { lat, lon } = await this.getCoordsFromPlace(this.manualPlace);
          this.form.location_coords = `${lat},${lon}`;
          this.form.location = this.manualPlace;
        } else if (!this.autoLocation) {
          await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              pos => {
                const lat = pos.coords.latitude;
                const lon = pos.coords.longitude;
                this.autoLocation = { lat, lon };
                this.form.location_coords = `${lat},${lon}`;
                this.form.location = `${lat},${lon}`;
                resolve();
              },
              err => {
                console.log('Location error: ' + err.message);
                reject(err);
              }
            );
          });
        }

        // Submit to backend
        const { data } = await axios.post(
          `${backendUrl}/api/auth/signup`,
          this.form,
          { withCredentials: true }
        );

        console.log('Signup successful!');
        this.$emit('user-registered', data.user);
        this.$emit('change-page', 'DashboardPage');
      } catch (err) {
        console.error('Signup error:', err.response?.data || err);
        console.log(err.response?.data?.message || 'Signup failed');
      }
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.signup-page {
  min-height: 100vh;
  background: #fffbe8;
}

/* ── Page header ── */
.page-header {
  max-width: 460px;
  margin: 0 auto;
  padding: 40px 20px 0 24px;
}

.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
}

.page-sub {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
  margin-top: 4px;
}

/* ── Page wrapper ── */
.page-wrapper {
  max-width: 460px;
  margin: 0 auto;
  padding: 24px 20px 60px;
}

/* ── Form card ── */
.form-card {
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid #f0e0ff;
  padding: 28px 24px;
  box-shadow: 0 4px 24px rgba(186, 113, 255, 0.08);
}

/* ── Form groups ── */
.form-group {
  margin-bottom: 22px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.form-input:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.form-input::placeholder { color: #c9b8d8; }

/* ── Location ── */
.location-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
  cursor: pointer;
}

.location-btn {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  background: #fffbe8;
  color: #4a1070;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
}

.location-btn:hover {
  background: #eac7ff;
  border-color: #ba71ff;
}

.location-success {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1a6b3c;
}

/* ── Pill selectors ── */
.pill-group {
  display: flex;
  gap: 8px;
}

.pill-option {
  flex: 1;
  text-align: center;
  padding: 10px 18px;
  border-radius: 999px;
  border: 1.5px solid #f0e0ff;
  background: #fffbe8;
  color: #888;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  font-family: inherit;
}

.pill-option:hover {
  border-color: #ba71ff;
  color: #4a1070;
}

.pill-option.pill-active {
  background: #eac7ff;
  border-color: #ba71ff;
  color: #4a1070;
}

/* ── Submit button ── */
.submit-btn {
  width: 100%;
  padding: 14px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 6px;
  font-family: inherit;
}

.submit-btn:hover { background: #ba71ff; color: #fff; }
.submit-btn:active { transform: scale(0.98); }

/* ── Hamburger ── */
.hamburger {
  position: fixed;
  top: 20px;
  left: 20px;
  font-size: 28px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1000;
  color: #4a1070;
}

/* ── Sidebar ── */
.sidebar {
  width: 240px;
  background-color: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  position: fixed;
  top: 0;
  left: -240px;
  height: 100%;
  transition: left 0.3s ease;
  z-index: 1000;
}

.sidebar.open { left: 0; }

.sidebar-header {
  background: linear-gradient(135deg, #eac7ff 0%, #dda6fd 100%);
  padding: 20px;
  color: #4a1070;
  position: relative;
  overflow: hidden;
}

.sidebar-header::before,
.sidebar-header::after {
  content: '';
  position: absolute;
  width: 30px;
  height: 30px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
}

.sidebar-header::before {
  top: 10px;
  left: 10px;
  transform: rotate(45deg);
}

.sidebar-header::after {
  top: 40px;
  right: 20px;
  transform: rotate(-15deg);
}

.sidebar-header p {
  position: relative;
  z-index: 1;
  margin: 4px 0;
  font-weight: 600;
  color: #4a1070;
}

.close-btn {
  background: none;
  border: none;
  font-size: 22px;
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #4a1070;
  z-index: 2;
}

.sidebar-menu {
  list-style: none;
  padding: 16px 20px 20px;
}

.sidebar-menu li {
  padding: 20px 0;
  cursor: pointer;
  font-size: 15px;
  color: #333;
  border-bottom: 1px solid #fdf5ff;
  transition: all 0.2s ease;
}

.sidebar-menu li:last-child { border-bottom: none; }

.sidebar-menu li:hover {
  color: #ba71ff;
  transform: translateX(4px);
}

/* ── Responsive ── */
@media (max-width: 480px) {
  .page-wrapper { padding: 20px 14px 40px; }
  .form-card { padding: 20px 16px; }
}
</style>
